import { Link } from "react-router-dom";

import { DisclosureRow, Money, Notice, Status, Surface, cn, focusRing } from "../../../../components/ui";
import { BasicPayWorking, datesText } from "../payslips/PayCalculation";
import { classificationLabel, eventLabel, stateOf, taxLabel, when } from "./adjustmentView";

// Report 54B (D2): one correction, read only. The same view for HR, Finance and (without staff names) the employee.
// It shows the original payslip figure beside the corrected one for each component that changed, the tax treatment,
// the money that really moved, and the history. Nothing here is calculated: every figure is the server's.

const table = "mt-2 w-full border-collapse border-t border-default text-body-sm";

function Fact({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-meta text-secondary">{label}</dt>
      <dd className="mt-0.5 break-words text-body-sm font-medium text-foreground">{children}</dd>
    </div>
  );
}

const signed = (cents) => (Number(cents) > 0 ? "+" : "");

/** Original, corrected and difference for each component that changed. */
export function BeforeAfter({ lines, caption = "What changes" }) {
  return (
    <div className="overflow-x-auto">
      <table data-ui="payslip-table" className={cn(table, "min-w-[28rem]")}>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-subtle text-cell-head text-secondary">
            <th scope="col" className="py-2 pr-4 text-left">Component</th>
            <th scope="col" className="py-2 pl-3 text-right">On the payslip</th>
            <th scope="col" className="py-2 pl-3 text-right">Corrected</th>
            <th scope="col" className="py-2 pl-3 text-right">Difference</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={`${line.component}-${index}`} className="border-b border-subtle align-top">
              <th scope="row" className="whitespace-normal py-2 pr-4 text-left font-normal text-foreground">
                {line.label}
                {line.basis ? <span className="mt-0.5 block text-meta text-secondary">{line.basis}</span> : null}
                {line.dates?.length ? <span className="mt-0.5 block text-meta text-secondary">{line.dates.length === 1 ? "Date" : "Dates"}: {datesText(line.dates)}</span> : null}
              </th>
              <td className="whitespace-nowrap py-2 pl-3 text-right"><Money value={line.originalCents} /></td>
              <td className="whitespace-nowrap py-2 pl-3 text-right"><Money value={line.correctedCents} /></td>
              <td className="whitespace-nowrap py-2 pl-3 text-right font-medium text-foreground">{signed(line.differenceCents)}<Money value={line.differenceCents} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Gross change, less the change in what is withheld, is what the employee is owed or owes. */
export function GrossToNetDifference({ item }) {
  const decided = item.statutoryState !== "REVIEW_REQUIRED";
  return (
    <dl className="mt-2 max-w-md space-y-1 text-body-sm">
      <div className="flex justify-between gap-3"><dt className="text-secondary">Change in gross pay</dt><dd>{signed(item.grossDifferenceCents)}<Money value={item.grossDifferenceCents} /></dd></div>
      <div className="flex justify-between gap-3">
        <dt className="text-secondary">Change in contributions and tax withheld</dt>
        <dd>{decided ? <>{signed(item.statutoryDifferenceCents)}<Money value={item.statutoryDifferenceCents || 0} /></> : <span className="text-secondary">Not decided yet</span>}</dd>
      </div>
      <div className="flex justify-between gap-3 border-t border-subtle pt-1 font-semibold text-foreground">
        <dt>{item.direction === "PAY_EMPLOYEE" ? "To pay the employee" : "To recover from the employee"}</dt>
        <dd>{decided ? <Money value={item.amountCents} /> : <span className="font-normal text-secondary">Not decided yet</span>}</dd>
      </div>
    </dl>
  );
}

const MOVEMENT = {
  CASH_HANDOVER: "Cash handed to the employee",
  CASH_RETURN: "Cash received from the employee",
  PAYROLL_RECOVERY: "Installment taken with a paid salary",
  LOAN_BALANCE_RESTORE: "Put back on the loan balance",
};

/**
 * `audience`: "staff" shows who raised and decided; "employee" leaves staff names out.
 * `payslipTo`: where the original payslip opens for this reader.
 */
export default function CorrectionDetail({ item, audience = "staff", payslipTo }) {
  const state = stateOf(item);
  const staff = audience === "staff";
  const pay = item.direction === "PAY_EMPLOYEE";
  return (
    <div className="space-y-6">
      <Surface padded>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
          {staff ? <Fact label="Employee">{[item.memberName, item.employeeCode].filter(Boolean).join(" · ")}</Fact> : null}
          <Fact label="Original payslip">
            {payslipTo ? <Link to={payslipTo} className={cn("rounded-control text-accent hover:underline", focusRing)}>{item.payslipNumber || "Open payslip"}</Link> : item.payslipNumber}
            <span className="block font-normal text-secondary">{item.periodName}</span>
          </Fact>
          <Fact label="Kind">{classificationLabel(item.classification)}</Fact>
          <Fact label={pay ? "Owed to the employee" : "Claimed from the employee"}>
            {item.statutoryState === "REVIEW_REQUIRED" ? <span className="font-normal text-secondary">Not decided yet</span> : <Money value={item.amountCents} />}
          </Fact>
          <Fact label={pay ? "Paid so far" : "Recovered so far"}><Money value={item.settledCents} /></Fact>
          <Fact label="Still outstanding"><Money value={item.outstandingCents} /></Fact>
        </dl>
        <p className="mt-4 border-t border-subtle pt-3 text-body-sm">
          <Status tone={state.tone} icon={state.tone !== "neutral"} className="font-medium">{state.label}</Status>
          {staff && state.next ? <span className="mt-0.5 block text-secondary">{state.next}</span> : null}
        </p>
      </Surface>

      <Surface padded>
        <h2 className="text-section-title text-foreground">Why and what changes</h2>
        <p className="mt-1 max-w-[75ch] text-body-sm text-foreground">{item.reason}</p>
        {item.evidenceReference ? <p className="mt-1 text-body-sm text-secondary">Evidence: {item.evidenceReference}</p> : null}
        <p className="mt-1 text-meta text-secondary">
          {item.calculationMode === "AUTOMATIC"
            ? "Worked out by the payroll formula from the figures stored with the payslip."
            : "Entered by a person with the stated basis; not worked out automatically."}
          {" "}The original payslip is not changed.
        </p>
        <BeforeAfter lines={item.lines} />
        <GrossToNetDifference item={item} />
        {item.restoresLoanBalance ? <p className="mt-2 text-meta text-secondary">When the refund is paid, the same amount goes back on the loan balance.</p> : null}
      </Surface>

      <Surface padded>
        <h2 className="text-section-title text-foreground">Tax and contributions</h2>
        <p className="mt-1 text-body-sm">
          <Status tone={item.statutoryState === "REVIEW_REQUIRED" ? "warning" : "neutral"} icon={item.statutoryState === "REVIEW_REQUIRED"} className="font-medium">{taxLabel(item)}</Status>
        </p>
        {item.statutoryState === "REVIEW_REQUIRED" ? (
          <p className="mt-1 max-w-[75ch] text-body-sm text-secondary">
            A change in pay can change SSS, PhilHealth, Pag-IBIG and withholding tax, sometimes on another payslip of the same month.
            Until an authorised reviewer records the treatment, this correction is held: it cannot be approved, paid or recovered.
          </p>
        ) : (
          <p className="mt-1 max-w-[75ch] text-body-sm text-secondary">
            {item.statutoryBasis}
            {staff && item.statutoryReviewedByName ? ` — ${item.statutoryReviewedByName}, ${when(item.statutoryReviewedAt)}` : ""}
          </p>
        )}
        {item.statutoryState === "REVIEWED" ? (
          <p className="mt-1 text-meta text-tertiary">This records how the correction was treated here. It does not show that a government filing or remittance was amended.</p>
        ) : null}
      </Surface>

      {!pay ? (
        <Surface padded>
          <h2 className="text-section-title text-foreground">How it is recovered</h2>
          <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
            <Fact label="Method">{item.recoveryMethod === "FUTURE_PAY" ? "From future pay, in installments" : "Cash returned by the employee"}</Fact>
            {item.recoveryMethod === "FUTURE_PAY" ? <Fact label="Each payroll"><Money value={item.installmentCents} /></Fact> : null}
            {item.recoveryMethod === "FUTURE_PAY" && staff ? <Fact label="Employee's authorisation">{item.employeeAuthorizationRef}</Fact> : null}
          </dl>
          {item.recoveryBasis ? <p className="mt-2 max-w-[75ch] text-body-sm text-secondary">Basis: {item.recoveryBasis}</p> : null}
          {item.recoveryPaused ? <Notice tone="warning" className="mt-3">Installments are paused while the dispute is open.</Notice> : null}
          {item.employeeResponse === "DISPUTED" ? <p className="mt-2 max-w-[75ch] text-body-sm"><Status tone="danger" icon>Disputed</Status><span className="block text-secondary">{item.disputeReason}</span></p> : null}
          {item.disputeResolution ? <p className="mt-2 max-w-[75ch] text-body-sm text-secondary">Finance's answer: {item.disputeResolution}</p> : null}
          {item.employeeResponse === "ACKNOWLEDGED" ? <p className="mt-2 text-body-sm text-secondary">The employee acknowledged this claim. An acknowledgement is not a payment.</p> : null}
        </Surface>
      ) : null}

      <Surface padded>
        <h2 className="text-section-title text-foreground">Money that moved</h2>
        {item.movements.length ? (
          <ul aria-label="Money that moved" className="mt-2 divide-y divide-subtle text-body-sm">
            {item.movements.map((move) => (
              <li key={move.id} className="flex items-baseline justify-between gap-3 py-2">
                <span className="min-w-0">
                  <span className="text-foreground">{MOVEMENT[move.type] || move.type}</span>
                  <span className="block text-meta text-secondary">
                    {[when(move.createdAt), move.reference ? `ref. ${move.reference}` : null,
                      move.type === "CASH_HANDOVER" ? (move.status === "COMPLETED" ? (move.confirmationKind === "owner_on_behalf" ? `confirmed by the owner for the employee: ${move.confirmationReason}` : `confirmed by the employee ${when(move.confirmedAt)}`) : "not yet confirmed by the employee") : null,
                    ].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <Money value={move.amountCents} className="shrink-0 text-money text-foreground" />
              </li>
            ))}
          </ul>
        ) : <p className="mt-1 text-body-sm text-secondary">No money has moved on this correction yet.</p>}
      </Surface>

      {item.original?.basic ? (
        <Surface padded={false}>
          <div className="px-4">
            <DisclosureRow label="How the basic pay was worked out" hint={item.proposed ? "On the payslip, and with the correction" : "On the original payslip"}>
              <div className={cn("grid gap-x-8 gap-y-5 pb-4", item.proposed ? "lg:grid-cols-2" : "")}>
                <section>
                  <h3 className="text-label text-foreground">On the payslip</h3>
                  <BasicPayWorking calculation={item.original} caption="Basic pay on the payslip" />
                </section>
                {item.proposed ? (
                  <section>
                    <h3 className="text-label text-foreground">With the correction</h3>
                    <BasicPayWorking calculation={item.proposed} caption="Basic pay with the correction" />
                  </section>
                ) : null}
              </div>
            </DisclosureRow>
          </div>
        </Surface>
      ) : null}

      <Surface padded>
        <h2 className="text-section-title text-foreground">History</h2>
        <ol className="mt-2 space-y-2 text-body-sm">
          {item.events.map((event, index) => (
            <li key={`${event.at}-${index}`}>
              <span className="text-foreground">{eventLabel(event.action)}</span>
              <span className="text-secondary">
                {" · "}{[staff ? event.actorName : null, when(event.at)].filter(Boolean).join(", ")}
                {event.amountCents != null && event.action !== "STATUTORY_REVIEWED" ? <>{" · "}<Money value={event.amountCents} /></> : null}
              </span>
              {event.note ? <span className="block text-meta text-secondary">{event.note}</span> : null}
            </li>
          ))}
        </ol>
      </Surface>
    </div>
  );
}
